
// Centralized sync state management for menu operations
export const globalSyncState = {
  isMenuSyncing: false,
  lastSyncTime: 0,
  pendingSyncTimeout: null as NodeJS.Timeout | null,
  syncAttempts: 0,
  maxSyncAttempts: 3, // Reduced from 5 to be more strict
  lastSavedMenuHash: '',
  lastMenuSyncRef: 0,
  debugLogs: process.env.NODE_ENV === 'development', // Only enable debug logs in development
  // Counter to avoid update loops
  updateCounter: 0,
  // Maximum number of updates allowed in a short period
  maxUpdatesThreshold: 15, // Reduced from 30 to prevent loops
  // Time period to reset counter (ms)
  updateCounterResetPeriod: 60000, // Increased from 30s to 1 min
  // Last time the counter was reset
  lastCounterReset: 0,
  // Control of delete operations to avoid duplicates
  lastDeleteOperation: '',
  lastDeleteTime: 0,
  // Control of initial load
  initialLoadComplete: false,
  // Minimum time between forced menu updates (ms) - Allow immediate refreshes for manual operations
  minManualRefreshInterval: 500, // Decreased from 2000ms to 500ms for much quicker manual refreshes
  // Track last manual refresh request time
  lastManualRefreshRequest: 0,
  // Event count by type
  eventCounts: {} as Record<string, number>,
  // List of temporarily blocked events
  blockedEvents: new Set<string>(),
  // Flag for first page load
  firstPageLoadComplete: false,
  // Flag to block all events during critical operations
  criticalOperationInProgress: false,
  // Current priority operation
  currentPriorityOperation: null as string | null,
  // Shopping list generation status
  shoppingListNeedsUpdate: false,
  // Circuit breaker to detect and stop infinite loops
  consecutiveOperations: 0,
  maxConsecutiveOperations: 10,
  lastOperationTime: 0,
  operationTimeThreshold: 2000, // 2 seconds
};

// Helper function to hash a menu for comparison
export const hashMenu = (menu: any | null): string => {
  if (!menu) return '';
  try {
    // Simplify the menu to just what matters for comparison
    const simplifiedMenu = {
      days: menu.days,
      nutritionGoals: menu.nutritionGoals
    };
    return JSON.stringify(simplifiedMenu);
  } catch (error) {
    console.error('[MENU] Error hashing menu:', error);
    return Date.now().toString(); // Fallback to timestamp to indicate change
  }
};

// Function to control update frequency
export const shouldAllowUpdate = (source: string = 'unknown'): boolean => {
  const now = Date.now();

  // Circuit breaker pattern to detect and prevent infinite loops
  if (now - globalSyncState.lastOperationTime < globalSyncState.operationTimeThreshold) {
    globalSyncState.consecutiveOperations++;
    
    if (globalSyncState.consecutiveOperations > globalSyncState.maxConsecutiveOperations) {
      console.warn(`[MENU] Circuit breaker triggered - too many operations in short time window`);
      return false;
    }
  } else {
    // Reset consecutive operations counter if enough time has passed
    globalSyncState.consecutiveOperations = 0;
  }
  
  globalSyncState.lastOperationTime = now;
  
  // Always allow manual refresh operations, even if frequent
  if (source === 'manual-refresh') {
    // Just update the timestamp but don't block
    globalSyncState.lastManualRefreshRequest = now;
    return true;
  }
  
  // Always allow priority operations
  if (source === globalSyncState.currentPriorityOperation) {
    if (globalSyncState.debugLogs) {
      console.log(`[MENU] Allowing priority operation: ${source}`);
    }
    return true;
  }

  // Always allow initial load
  if (source === 'initial-load' && !globalSyncState.firstPageLoadComplete) {
    globalSyncState.firstPageLoadComplete = true;
    return true;
  }
  
  // Never block direct user operations or shopping list related operations
  if (
    source === 'user-action' || 
    source === 'recipe-add' || 
    source === 'recipe-remove' ||
    source === 'shopping-list-update' ||
    source === 'add-ingredient'
  ) {
    if (globalSyncState.debugLogs) {
      console.log(`[MENU] Allowing important user operation: ${source}`);
    }
    return true;
  }
  
  // Reset counter after enough time
  if (now - globalSyncState.lastCounterReset > globalSyncState.updateCounterResetPeriod) {
    globalSyncState.updateCounter = 0;
    globalSyncState.lastCounterReset = now;
    globalSyncState.blockedEvents.clear(); // Clear blocked events
    if (globalSyncState.debugLogs) {
      console.log('[MENU] Reset update counter due to time elapsed');
    }
  }
  
  // Increment counter only for automated events
  if (source !== 'user-action' && source !== 'manual-refresh') {
    globalSyncState.updateCounter++;
  }
  
  // Check if threshold is exceeded
  if (globalSyncState.updateCounter > globalSyncState.maxUpdatesThreshold) {
    return false;
  }
  
  return true;
};

// Function to check if we should sync the menu based on elapsed time
export const shouldSyncMenu = (minInterval: number = 500): boolean => { // Reduced to 500ms for more responsive syncs
  const now = Date.now();
  if (globalSyncState.isMenuSyncing) {
    return false;
  }
  
  if (now - globalSyncState.lastSyncTime < minInterval) {
    return false;
  }
  
  // Always allow first sync after reset
  if (globalSyncState.updateCounter <= 1) {
    return true;
  }
  
  // Allow sync if shopping list needs update
  if (globalSyncState.shoppingListNeedsUpdate) {
    if (globalSyncState.debugLogs) {
      console.log('[MENU] Allowing sync due to shopping list needing update');
    }
    globalSyncState.shoppingListNeedsUpdate = false;
    return true;
  }
  
  return shouldAllowUpdate();
};

// Function to control specific events
export const shouldProcessEvent = (eventName: string): boolean => {
  const now = Date.now();
  
  // Always allow manual refresh events
  if (eventName === 'manual-refresh') {
    return true;
  }
  
  // Always allow initial load event
  if (eventName === 'initial-load' && !globalSyncState.initialLoadComplete) {
    return true;
  }
  
  // Reject if event is blocked
  if (globalSyncState.blockedEvents.has(eventName)) {
    return false;
  }
  
  // Increment counter for this event type
  globalSyncState.eventCounts[eventName] = (globalSyncState.eventCounts[eventName] || 0) + 1;
  
  // Block event if too many occurrences
  if (globalSyncState.eventCounts[eventName] > 8) { // Reduced from 15 to 8
    if (globalSyncState.debugLogs) {
      console.log(`[MENU] Blocking event ${eventName} due to high frequency (${globalSyncState.eventCounts[eventName]} times)`);
    }
    globalSyncState.blockedEvents.add(eventName);
    
    // Schedule unblocking after a time
    setTimeout(() => {
      globalSyncState.blockedEvents.delete(eventName);
      globalSyncState.eventCounts[eventName] = 0;
      if (globalSyncState.debugLogs) {
        console.log(`[MENU] Unblocked event ${eventName}`);
      }
    }, 30000); // 30 seconds block (increased from 20s)
    
    return false;
  }
  
  // Allow shopping list update events
  if (eventName === 'shopping-list-update') {
    globalSyncState.shoppingListNeedsUpdate = true;
    return true;
  }
  
  return shouldAllowUpdate(eventName);
};

// Function to control delete operations
export const shouldAllowDeleteOperation = (identifier: string): boolean => {
  const now = Date.now();
  
  // If same operation and not enough time passed, don't allow it
  if (
    identifier === globalSyncState.lastDeleteOperation && 
    now - globalSyncState.lastDeleteTime < 3000 // Increased from 2s to 3s
  ) {
    return false;
  }
  
  // Record this operation
  globalSyncState.lastDeleteOperation = identifier;
  globalSyncState.lastDeleteTime = now;
  
  return true; // Always allow deletes that aren't duplicates
};

// Set a priority operation temporarily
export const setPriorityOperation = (operationName: string | null, timeoutMs: number = 5000): void => {
  if (globalSyncState.debugLogs) {
    console.log(`[MENU] Setting priority operation: ${operationName}`);
  }
  
  globalSyncState.currentPriorityOperation = operationName;
  
  if (operationName === 'shopping-list-update') {
    globalSyncState.shoppingListNeedsUpdate = true;
  }
  
  if (operationName) {
    setTimeout(() => {
      if (globalSyncState.currentPriorityOperation === operationName) {
        globalSyncState.currentPriorityOperation = null;
        if (globalSyncState.debugLogs) {
          console.log(`[MENU] Priority operation timeout: ${operationName}`);
        }
      }
    }, timeoutMs);
  }
};
